import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Stack, TextField, Typography,
} from '@mui/material';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { AccountService } from '../../services/account.service';
import { ExpenseClaimDTO } from '../../interfaces/expense.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { formatAmount } from '../../utils/expenseDisplay';
import { depositAccountOptions } from '../../utils/postingAccounts';

interface Props {
    claim: ExpenseClaimDTO;
    open: boolean;
    onClose: () => void;
    onPaid: (claim: ExpenseClaimDTO) => void;
}

const ExpensePayDialog: React.FC<Props> = ({ claim, open, onClose, onPaid }) => {
    const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
    const [accountId, setAccountId] = useState('');
    const [reference, setReference] = useState('');
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (open) { setSubmitted(false); loadAccounts(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const loadAccounts = async () => {
        try {
            const accounts = await AccountService.getAccountsByType('ASSET');
            setBankAccounts(depositAccountOptions(accounts));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load cash/bank accounts');
        }
    };

    const handlePay = async () => {
        setSubmitted(true);
        if (!accountId) {
            toast.showError('Pick a cash/bank account');
            return;
        }
        if (!reference.trim()) {
            toast.showError('Enter a payment reference (cheque number or transaction ID) before paying');
            return;
        }
        setBusy(true);
        try {
            const next = await ExpenseClaimService.payStandalone(claim.id, accountId, reference.trim());
            toast.showSuccess('Payment recorded');
            onPaid(next);
            onClose();
        } catch (e: any) {
            const data = e?.response?.data;
            const msg = data?.message ?? (typeof data === 'string' ? data : null);
            toast.showError(msg ?? 'Payment failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'expense.pay.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Pay claim: {claim.title}</DialogTitle>
            <DialogContent dividers>
                <Stack data-help-section="expense.pay.section.details" spacing={2} sx={{ mt: 1 }}>
                    <Typography>
                        Net reimbursable: <strong>{formatAmount(claim.netReimbursable ?? claim.totalAmount)}</strong>
                    </Typography>
                    <AccountAutocomplete
                        accounts={bankAccounts}
                        value={accountId}
                        onChange={setAccountId}
                        data-help="expense.pay.field.account" label="Pay from account"
                    />
                    <TextField
                        data-help="expense.pay.field.reference" label="Payment reference (e.g. cheque or txn ID)" fullWidth required
                        value={reference} onChange={e => setReference(e.target.value)}
                        error={submitted && !reference.trim()}
                        helperText={submitted && !reference.trim() ? 'Payment reference is required' : undefined}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="expense.pay.cancel" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button data-help="expense.pay.save" variant="contained" onClick={handlePay} disabled={busy}>
                    {busy ? 'Paying…' : 'Pay now'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpensePayDialog;
